import dayjs from 'dayjs';

/**
 * Atajos de rango de fechas de toda la app.
 *
 * Viven aquí (y no dentro del selector) porque los usan dos sitios que tienen
 * que calcular lo mismo: el `DateRangePicker`, que pinta el atajo y emite sus
 * fechas, y el store global `@/stores/dateRange`, que guarda el atajo elegido
 * (no las fechas) para que "Últimos 7 días" avance con el calendario.
 */

export type DateRangePreset = 'last-7d' | 'last-30d' | 'mtd' | 'qtd' | 'ytd';

export interface DateRange {
  start: Date | null;
  end: Date | null;
}

export const DATE_RANGE_PRESETS: readonly DateRangePreset[] = [
  'last-7d',
  'last-30d',
  'mtd',
  'qtd',
  'ytd',
];

export const DATE_RANGE_PRESET_LABELS: Record<DateRangePreset, string> = {
  'last-7d': 'Últimos 7 días',
  'last-30d': 'Últimos 30 días',
  mtd: 'Mes en curso',
  qtd: 'Trimestre',
  ytd: 'Año en curso',
};

export function isDateRangePreset(value: unknown): value is DateRangePreset {
  return typeof value === 'string' && (DATE_RANGE_PRESETS as readonly string[]).includes(value);
}

/** Solo los "Últimos N días" dependen de "Incluir hoy"; los periodos en curso acaban hoy. */
export function presetUsesIncludeToday(
  preset: DateRangePreset | null | undefined,
): preset is 'last-7d' | 'last-30d' {
  return preset === 'last-7d' || preset === 'last-30d';
}

const LAST_DAYS: Record<'last-7d' | 'last-30d', number> = { 'last-7d': 7, 'last-30d': 30 };

/**
 * Fechas de un atajo en el día de `now` (medianoche local).
 *
 * "Últimos N días" sigue la convención de Shopify Admin (confirmada con
 * capturas el 2026-05-19): empieza siempre en `hoy - N` y acaba hoy si
 * "Incluir hoy" está marcado o ayer si no. Con hoy son N + 1 días naturales.
 */
export function resolvePresetRange(
  preset: DateRangePreset,
  includeToday: boolean,
  now: Date = new Date(),
): { start: Date; end: Date } {
  const today = dayjs(now).startOf('day');
  if (presetUsesIncludeToday(preset)) {
    return {
      start: today.subtract(LAST_DAYS[preset], 'day').toDate(),
      end: (includeToday ? today : today.subtract(1, 'day')).toDate(),
    };
  }
  switch (preset) {
    case 'mtd':
      return { start: today.startOf('month').toDate(), end: today.toDate() };
    case 'qtd': {
      const startMonth = Math.floor(today.month() / 3) * 3;
      return { start: today.month(startMonth).startOf('month').toDate(), end: today.toDate() };
    }
    case 'ytd':
      return { start: today.startOf('year').toDate(), end: today.toDate() };
  }
}
